import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PackageCard } from "./card/PackageCard";
import { Product } from "./types";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { useMembership } from "@/contexts/MembershipContext";
import {
  isBloodTestVisible,
  isBaselineBloodTest,
  isFollowupBloodTest,
} from "@/utils/bloodTestVisibility";
import { MembershipBloodTestSection } from "@/components/membership/MembershipBloodTestSection";

interface PackageListProps {
  products: Product[];
  selectedProductId: string | null;
  onSelectPackage: (productId: string) => void;
  language: BookingLanguage;
  selectedAddOns: string[];
  onAddOnToggle: (productId: string) => void;
  onSelectAndContinue?: (productId: string) => void; // New prop for modal CTA
  /** Scan cards only: pick the package AND attach a membership. */
  onSelectAsMember?: (productId: string) => void;
  allProducts?: Product[];
}

export const PackageList: React.FC<PackageListProps> = ({
  products,
  selectedProductId,
  onSelectPackage,
  language,
  selectedAddOns,
  onAddOnToggle,
  onSelectAndContinue,
  onSelectAsMember,
  allProducts,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { isMember, currentEntitlementType, showAllBloodTestsFlag } =
    useMembership();

  // Member blood test layout: detect if this is a blood-only tab with member products
  const hasBloodTestProducts = products.some(
    (p) =>
      p.product_type === "package" &&
      (isBaselineBloodTest(p.stripe_product_id) ||
        isFollowupBloodTest(p.stripe_product_id)),
  );

  // Deep-linking the follow-up product (?products=<followup-id>) reveals it to a
  // non-member too — same as showAllBloodTestsFlag, just keyed off the selection.
  const followupSelected = products.some(
    (p) =>
      p.id === selectedProductId && isFollowupBloodTest(p.stripe_product_id),
  );

  if (
    (isMember || showAllBloodTestsFlag || followupSelected) &&
    hasBloodTestProducts
  ) {
    // Get all package products including membership-status ones
    const allBloodPackages = products.filter(
      (p) =>
        p.product_type === "package" &&
        (p.status === "active" || p.status === "membership"),
    );

    return (
      <MembershipBloodTestSection
        products={allBloodPackages}
        selectedProductId={selectedProductId}
        onSelectPackage={onSelectPackage}
        language={language}
        onSelectAndContinue={
          onSelectAndContinue
            ? (id: string) => onSelectAndContinue(id)
            : undefined
        }
        allProducts={allProducts ?? products}
      />
    );
  }

  // Filter for package products and preserve the sorting from the database
  // Members can see membership-status products (e.g. follow-up blood test)
  const packages = products
    .filter((product) => {
      if (product.product_type !== "package") return false;
      if (product.status !== "active" && product.status !== "membership")
        return false;
      if (
        product.status === "membership" &&
        !isMember &&
        !showAllBloodTestsFlag
      )
        return false;
      // Blood test visibility (baseline vs follow-up) driven by entitlement
      return isBloodTestVisible(product.stripe_product_id, {
        isMember,
        currentEntitlementType,
        showAllBloodTestsFlag,
      });
    })
    .sort((a, b) => {
      // Sort by sort_order if available (this is the main sorting from database)
      if (a.sort_order !== undefined && b.sort_order !== undefined) {
        const result = a.sort_order - b.sort_order;

        return result;
      }

      // Fallback to display_order if sort_order is not available
      if (a.display_order !== undefined && b.display_order !== undefined) {
        const result = a.display_order - b.display_order;

        return result;
      }

      // Final fallback to alphabetical by name
      const result = a.name.localeCompare(b.name);

      return result;
    });

  // Removed auto-selection to let users start with empty state and see USPs

  if (packages.length === 0) {
    return (
      <div className="p-6 text-center bg-gray-100 rounded-lg">
        <p className="text-gray-500">{t("productStep.noPackagesAvailable")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-0 flex flex-col lg:flex-row lg:justify-center lg:gap-4">
      {packages.map((product) => {
        const isSingle = packages.length === 1;
        const isHorizontalBlood =
          isSingle && product.package_types?.includes("blood-only");
        const wrapperClass = isHorizontalBlood
          ? "w-full lg:min-w-[780px] lg:max-w-[780px]"
          : isSingle
            ? "w-full lg:min-w-[500px] lg:max-w-[500px]"
            : "w-full lg:flex-1 lg:max-w-[380px]";
        return (
          <div className={wrapperClass} key={product.id}>
            <PackageCard
              product={product}
              isSelected={selectedProductId === product.id}
              onSelect={() => onSelectPackage(product.id)}
              language={language}
              onSelectAsMember={
                onSelectAsMember
                  ? () => onSelectAsMember(product.id)
                  : undefined
              }
              onSelectAndContinue={
                onSelectAndContinue
                  ? () => onSelectAndContinue(product.id)
                  : undefined
              }
              isOnlyProduct={packages.length === 1}
              allProducts={allProducts ?? products}
              horizontalLayout={isHorizontalBlood}
            />
          </div>
        );
      })}
    </div>
  );
};
